import { hexToRgbString } from '@/helpers/colorUtils'
import type { ThemeVariableMap } from '@/types/theme'
import log from '@/log'

/** RCL theme scope classes from twPlugin addBase (`.ihm` / `.unified` / `.wtlbl` / `.legacy-unified`). */
export const THEME_DOM_CLASSES = ['ihm', 'unified', 'wtlbl', 'legacy-unified'] as const

export type ThemeDomClass = (typeof THEME_DOM_CLASSES)[number]

const THEME_DESCENDANT_SELECTOR = THEME_DOM_CLASSES.map((c) => `.${c}`).join(', ')

/** How long to watch for late-mounted nested theme scopes under a specific root. */
const THEME_DESCENDANT_WATCH_MS = 30000

/**
 * Nav tokens must not stay as leftover inline primary stamps (reddens left drawer).
 * Cleared whenever we own a scope so RCL stylesheet defaults apply.
 */
const BUTTON_NAVIGATION_VAR_KEYS = [
  'button-navigation-text',
  'button-navigation-surface',
  'button-navigation-border',
  'button-navigation-icon',
  'button-navigation-surface--hover',
  'button-navigation-text--hover',
  'button-navigation-icon--hover',
  'button-navigation-surface--pressed',
  'button-navigation-text--pressed',
  'button-navigation-surface--selected',
  'button-navigation-text--selected',
] as const

/** Active document-level theme owner (portaled Filter footers, remounts). */
let documentThemeOwner: MutationObserver | null = null

/**
 * Sets CSS custom properties on a single element (inline, beats stylesheet `.ihm` defaults).
 */
function stampVarsOnElement(el: HTMLElement, vars: Record<string, string>): void {
  Object.entries(vars).forEach(([key, value]) => el.style.setProperty(`--${key}`, value))
}

function clearButtonNavigationVars(el: HTMLElement): void {
  BUTTON_NAVIGATION_VAR_KEYS.forEach((key) => el.style.removeProperty(`--${key}`))
}

/**
 * Forces host theme class on one element (strips other RCL scope classes first).
 */
function setThemeClassOnElement(el: HTMLElement, themeClass: ThemeDomClass): void {
  THEME_DOM_CLASSES.forEach((c) => el.classList.remove(c))
  el.classList.add(themeClass)
}

function isThemeScopeElement(el: HTMLElement): boolean {
  return THEME_DOM_CLASSES.some((c) => el.classList.contains(c))
}

/**
 * Applies host theme class + optional CTA vars to a theme-scope node.
 */
function ownThemeScopeElement(
  el: HTMLElement,
  themeClass: ThemeDomClass,
  vars?: Record<string, string>
): void {
  setThemeClassOnElement(el, themeClass)
  clearButtonNavigationVars(el)
  if (vars) stampVarsOnElement(el, vars)
}

/**
 * Stamps vars on `root` and every descendant with an RCL theme scope class.
 * Nested `.ihm` / `.unified` redefine CTA tokens via stylesheet; inline on those
 * nodes is required so Export / CTA buttons inherit host primary (e.g. IHM red).
 */
function applyVarsToRootAndThemeDescendants(root: HTMLElement, vars: Record<string, string>): void {
  clearButtonNavigationVars(root)
  stampVarsOnElement(root, vars)
  root.querySelectorAll<HTMLElement>(THEME_DESCENDANT_SELECTOR).forEach((el) => {
    clearButtonNavigationVars(el)
    stampVarsOnElement(el, vars)
  })
}

function queryRoots(selector: string): HTMLElement[] {
  return selector === ':root' ? [document.documentElement] : Array.from(document.querySelectorAll<HTMLElement>(selector))
}

/**
 * Applies CSS variable overrides to matching DOM elements.
 * Supports direct RGB values and RGB + opacity format.
 *
 * @param selector - CSS selector (e.g. '.unified', ':root')
 * @param vars     - Resolved CSS variable map
 * @returns `true` if elements found and updated, `false` otherwise
 */
function applyToElements(selector: string, vars: Record<string, string>): boolean {
  const elements = queryRoots(selector)

  if (elements.length === 0) return false

  elements.forEach((el) => applyVarsToRootAndThemeDescendants(el, vars))

  return true
}

/**
 * Watches each matched root for newly added theme-scope descendants and stamps vars.
 * RCL microfrontends (Violations) mount nested `.ihm` after host `applyResolvedTheme`.
 */
function watchThemeDescendants(selector: string, vars: Record<string, string>): void {
  const roots = queryRoots(selector)
  if (roots.length === 0) return

  roots.forEach((root) => {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (!(node instanceof HTMLElement)) continue

          if (THEME_DOM_CLASSES.some((c) => node.classList.contains(c))) {
            stampVarsOnElement(node, vars)
          }
          node.querySelectorAll<HTMLElement>(THEME_DESCENDANT_SELECTOR).forEach((el) => stampVarsOnElement(el, vars))
        }
      }
    })

    observer.observe(root, { childList: true, subtree: true })

    setTimeout(() => {
      observer.disconnect()
      log.debug(`[applyTheme] stopped watching theme descendants under "${selector}"`)
    }, THEME_DESCENDANT_WATCH_MS)
  })
}

/**
 * Sets the RCL theme class on matching elements (removes other theme scope classes first).
 *
 * @returns `true` if elements found and updated, `false` otherwise
 */
function applyThemeClassToElements(selector: string, themeClass: ThemeDomClass): boolean {
  const elements = queryRoots(selector)

  if (elements.length === 0) return false

  elements.forEach((el) => {
    setThemeClassOnElement(el, themeClass)
  })

  return true
}

/**
 * Watches the DOM for the selector to appear, then runs `apply`.
 * Gives up after 30 seconds if element never appears.
 */
function watchAndApply(selector: string, apply: () => boolean, label: string): void {
  const observer = new MutationObserver(() => {
    if (apply()) {
      observer.disconnect()
      clearTimeout(timeout)
    }
  })

  observer.observe(document.body, { childList: true, subtree: true })

  const timeout = setTimeout(() => {
    observer.disconnect()
    log.debug(`[applyTheme] "${selector}" not found for ${label}, giving up.`)
  }, 30000)
}

/**
 * Resolves a ThemeVariableMap by converting hex colors to RGB strings.
 * Preserves opacity suffix if present (e.g. '#e63946 / 0.5' → '230 57 70 / 0.5').
 *
 * @param map - Raw variable map with hex colors
 * @returns Resolved map with RGB strings
 *
 * @example
 * resolveVariableMap({ 'button-surface': '#e63946 / 0.5' })
 * // { 'button-surface': '230 57 70 / 0.5' }
 */
export function resolveVariableMap(map: ThemeVariableMap): Record<string, string> {
  return Object.fromEntries(
    Object.entries(map).map(([key, value]) => {
      const [hex, opacity] = value.split('/').map((s) => s.trim())
      const rgb = hexToRgbString(hex)
      return [key, opacity ? `${rgb} / ${opacity}` : rgb]
    })
  )
}

/**
 * Entry point — resolves and applies a ThemeVariableMap to the DOM.
 * Falls back to MutationObserver if element not yet mounted.
 * Also stamps nested RCL theme scopes (`.ihm` / `.unified` / …) under each root,
 * and watches briefly for late-mounted nested scopes (Violations dashboard).
 *
 * @param selector - CSS selector to target (e.g. '.unified')
 * @param map      - Raw variable map with hex colors and optional opacity
 */
export function applyVariableMap(selector: string, map: ThemeVariableMap): void {
  const resolved = resolveVariableMap(map)

  const apply = (): boolean => {
    const ok = applyToElements(selector, resolved)
    if (ok) watchThemeDescendants(selector, resolved)
    return ok
  }

  if (!apply()) {
    watchAndApply(selector, apply, 'CSS vars')
  }
}

/**
 * Applies an RCL theme scope class (ihm / unified / wtlbl / legacy-unified) to matching elements.
 * Falls back to MutationObserver if element not yet mounted.
 *
 * @param selector   - CSS selector to target (e.g. '#ui-violations-dashboard-container')
 * @param themeClass - Theme class to set
 */
export function applyThemeClass(selector: string, themeClass: ThemeDomClass): void {
  if (!applyThemeClassToElements(selector, themeClass)) {
    watchAndApply(selector, () => applyThemeClassToElements(selector, themeClass), `class ${themeClass}`)
  }
}

/**
 * ui-dashboards owns all RCL theme scopes in the document: correct class + optional CTA vars.
 * Covers Radix portals on `body` and remounts (Filter dismiss/reopen). Page-lifetime observer.
 *
 * @param themeClass - Host-resolved scope (`ihm` | `unified` | …)
 * @param vars       - Already-resolved RGB CSS var map (from {@link resolveVariableMap}), if any
 */
export function ownDocumentThemeScopes(themeClass: ThemeDomClass, vars?: Record<string, string>): void {
  if (typeof document === 'undefined' || !document.body) return

  const ownTree = (node: HTMLElement): void => {
    if (isThemeScopeElement(node)) {
      ownThemeScopeElement(node, themeClass, vars)
    }
    node.querySelectorAll<HTMLElement>(THEME_DESCENDANT_SELECTOR).forEach((el) => {
      ownThemeScopeElement(el, themeClass, vars)
    })
  }

  documentThemeOwner?.disconnect()
  documentThemeOwner = null

  ownTree(document.body)

  documentThemeOwner = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of Array.from(mutation.addedNodes)) {
        if (!(node instanceof HTMLElement)) continue
        ownTree(node)
      }
    }
  })

  documentThemeOwner.observe(document.body, { childList: true, subtree: true })
}

/** Disconnects document theme owner (tests). */
export function stopOwningDocumentThemeScopes(): void {
  documentThemeOwner?.disconnect()
  documentThemeOwner = null
}
